import { useState, useEffect, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { PhoneField, PHONE_COUNTRIES, parsePhone as parsePhoneShared, serializePhone } from "./PhoneField";

/* PhoneField internally tracks country by its ISO code (e.g. "MX"),
   but our form state + the accept-invitation function speak in dial
   digits (e.g. "52"). These helpers bridge between the two. */
function dialToCountryCode(d) {
  var withPlus = "+" + String(d || "").replace(/\D/g, "");
  var hit = PHONE_COUNTRIES.find(function (c) { return c.dial === withPlus; });
  return hit ? hit.code : "MX";
}
function countryCodeToDialDigits(code) {
  var hit = PHONE_COUNTRIES.find(function (c) { return c.code === code; });
  return hit ? hit.dial.replace(/\D/g, "") : "52";
}

const NAVY = "#0D1B2A";
const NAVY_2 = "#152A40";
const ACCENT = "#5BBCB0";
const ACCENT_DARK = "#3D9B8F";

const T = {
  en: {
    title: "Complete your registration",
    sub: "You've been invited to GFI Nexus",
    validating: "Validating invitation...",
    invalidToken: "Invalid or missing invitation token",
    expired: "This invitation has expired",
    used: "This invitation has already been used or was revoked",
    notFound: "Invitation not found",
    firstName: "First name",
    lastName: "Last name",
    username: "Username",
    usernameHint: "Letters, numbers, period, hyphen. Min 3 characters.",
    email: "Your email",
    emailHint: "Where replies to your sent emails will go",
    phone: "Phone",
    countryCode: "Country",
    areaCode: "Area",
    number: "Number",
    dob: "Date of birth",
    dobHint: "You must be at least 18",
    password: "Password",
    passwordHint: "Min 8 characters",
    confirmPassword: "Confirm password",
    submit: "Complete registration",
    submitting: "Creating account...",
    success: "Almost there! Check your inbox.",
    successSub: "We sent a confirmation link to your email. Click it to activate your account, then sign in.",
    errPasswordsMatch: "Passwords don't match",
    errPasswordShort: "Password must be at least 8 characters",
    errPasswordWeak: "Password must have at least 8 characters, including uppercase, lowercase, number, and special character",
    showPassword: "Show password",
    hidePassword: "Hide password",
    passwordRules: "Min 8 chars, uppercase, lowercase, number, special character",
    errUsernameShort: "Username must be at least 3 characters",
    errUsernameInvalid: "Username can only contain letters, numbers, period, hyphen",
    errEmail: "Invalid email",
    errPhone: "Complete all phone fields",
    errAge: "You must be at least 18 years old",
  },
  es: {
    title: "Completa tu registro",
    sub: "Has sido invitado a GFI Nexus",
    validating: "Validando invitación...",
    invalidToken: "Token de invitación inválido o faltante",
    expired: "Esta invitación expiró",
    used: "Esta invitación ya fue utilizada o revocada",
    notFound: "Invitación no encontrada",
    firstName: "Nombre",
    lastName: "Apellido",
    username: "Usuario",
    usernameHint: "Letras, números, punto, guión. Mínimo 3 caracteres.",
    email: "Tu email",
    emailHint: "A donde llegarán las respuestas de los emails que envíes",
    phone: "Teléfono",
    countryCode: "País",
    areaCode: "Área",
    number: "Número",
    dob: "Fecha de nacimiento",
    dobHint: "Debes tener al menos 18 años",
    password: "Contraseña",
    passwordHint: "Mínimo 8 caracteres",
    confirmPassword: "Confirmar contraseña",
    submit: "Completar registro",
    submitting: "Creando cuenta...",
    success: "¡Ya casi! Revisa tu correo.",
    successSub: "Te enviamos un enlace de confirmación a tu correo. Haz clic para activar tu cuenta y luego inicia sesión.",
    errPasswordsMatch: "Las contraseñas no coinciden",
    errPasswordShort: "La contraseña debe tener al menos 8 caracteres",
    errPasswordWeak: "La contraseña debe tener al menos 8 caracteres, incluyendo mayúscula, minúscula, número y carácter especial",
    showPassword: "Mostrar contraseña",
    hidePassword: "Ocultar contraseña",
    passwordRules: "Mínimo 8 caracteres, mayúscula, minúscula, número, carácter especial",
    errUsernameShort: "El usuario debe tener al menos 3 caracteres",
    errUsernameInvalid: "Usuario solo letras, números, punto, guión",
    errEmail: "Email inválido",
    errPhone: "Completa los 3 campos de teléfono",
    errAge: "Debes tener al menos 18 años",
  },
};

function suggestUsername(firstName, lastName) {
  const f = String(firstName || "").trim().split(/\s+/)[0] || "";
  const l = String(lastName || "").trim().split(/\s+/)[0] || "";
  const clean = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "");
  return clean(f) + clean(l);
}

export default function RegisterPage({ lang, setLang }) {
  const t = T[lang] || T.es;
  const token = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("token") || "";
    } catch (e) { return ""; }
  }, []);

  const [phase, setPhase] = useState("validating");
  /* `invitation` holds the validated row; currently only used to gate
     rendering, but keeping it lets future UX show the inviter name etc.
     eslint-disable-next-line no-unused-vars — see comment above. */
  // eslint-disable-next-line no-unused-vars
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phoneCountryCode: "52",
    phoneAreaCode: "",
    phoneNumber: "",
    dateOfBirth: "",
    password: "",
    confirmPassword: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /* Validate token on mount */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setPhase("error"); setError(t.invalidToken); return; }
      try {
        const { data, error: e } = await supabase
          .from("user_invitations")
          .select("*")
          .eq("token", token)
          .maybeSingle();
        if (cancelled) return;
        if (e || !data) { setPhase("error"); setError(t.notFound); return; }
        if (data.status === "accepted" || data.status === "revoked") { setPhase("error"); setError(t.used); return; }
        if (data.expires_at && new Date(data.expires_at) < new Date()) { setPhase("error"); setError(t.expired); return; }
        setInvitation(data);
        setForm((f) => ({
          ...f,
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          username: suggestUsername(data.first_name, data.last_name),
          email: data.email || "",
        }));
        setPhase("form");
      } catch (err) {
        if (!cancelled) { setPhase("error"); setError(err.message || String(err)); }
      }
    })();
    return () => { cancelled = true; };
  }, [token, t.invalidToken, t.notFound, t.used, t.expired]);

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function calcAge(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  }

  async function handleSubmit() {
    setError("");
    if (form.username.length < 3) { setError(t.errUsernameShort); return; }
    if (!/^[a-z0-9._-]+$/.test(form.username.toLowerCase())) { setError(t.errUsernameInvalid); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError(t.errEmail); return; }
    if (!form.phoneCountryCode || !form.phoneAreaCode || !form.phoneNumber) { setError(t.errPhone); return; }
    const age = calcAge(form.dateOfBirth);
    if (age === null || age < 18) { setError(t.errAge); return; }
    const pwdValid =
      form.password.length >= 8 &&
      /[A-Z]/.test(form.password) &&
      /[a-z]/.test(form.password) &&
      /[0-9]/.test(form.password) &&
      /[^A-Za-z0-9]/.test(form.password);
    if (!pwdValid) { setError(t.errPasswordWeak); return; }
    if (form.password !== form.confirmPassword) { setError(t.errPasswordsMatch); return; }

    setSubmitting(true);
    try {
      const res = await fetch("/.netlify/functions/accept-invitation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token,
          username: form.username.toLowerCase(),
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          phoneCountryCode: form.phoneCountryCode,
          phoneAreaCode: form.phoneAreaCode,
          phoneNumber: form.phoneNumber,
          dateOfBirth: form.dateOfBirth,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || ("HTTP " + res.status));
        setSubmitting(false);
        return;
      }
      setPhase("success");
      /* Account is created INACTIVE — the user must click the
         verification link in their email before they can sign in.
         No auto-redirect: keep them on the success screen telling them
         to check their inbox. */
      setSubmitting(false);
    } catch (e) {
      setError(e.message || String(e));
      setSubmitting(false);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8,
    color: "#fff",
    fontSize: 13,
    fontFamily: "inherit",
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: ACCENT,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 6,
    display: "block",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, " + NAVY + " 0%, " + NAVY_2 + " 100%)",
      padding: "40px 20px",
      fontFamily: "'DM Sans', sans-serif",
      color: "#fff",
    }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        {/* Language toggle */}
        <div style={{ textAlign: "right", marginBottom: 16 }}>
          {["en", "es"].map((l) => (
            <button key={l} onClick={() => setLang(l)} style={{
              padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)",
              background: lang === l ? ACCENT : "transparent",
              color: lang === l ? NAVY : "rgba(255,255,255,0.7)",
              cursor: "pointer", marginLeft: 6, fontSize: 11, fontWeight: 600,
            }}>{l.toUpperCase()}</button>
          ))}
        </div>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <svg width="80" height="80" viewBox="-55 -55 110 110">
            <defs>
              <linearGradient id="regLogoGl" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
                <stop offset="40%" stopColor="rgba(255,255,255,0.05)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
              </linearGradient>
            </defs>
            <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35" />
            <rect x="-35" y="-35" width="70" height="70" rx="16" fill="url(#regLogoGl)" />
            <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill={ACCENT} letterSpacing="3">GFI</text>
            <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill={ACCENT_DARK} />
            <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill={ACCENT_DARK} letterSpacing="3">NEXUS</text>
          </svg>
        </div>

        {phase === "validating" && (
          <div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>{t.validating}</div>
        )}

        {phase === "error" && (
          <div style={{
            padding: 24, background: "rgba(231,76,60,0.12)", border: "1px solid rgba(231,76,60,0.4)",
            borderRadius: 12, textAlign: "center", color: "#FCA5A5",
          }}>{error}</div>
        )}

        {phase === "success" && (
          <div style={{
            padding: 32, background: "rgba(91,188,176,0.12)", border: "1px solid " + ACCENT,
            borderRadius: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: ACCENT, marginBottom: 8 }}>{t.success}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{t.successSub}</div>
          </div>
        )}

        {phase === "form" && (
          <div style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12,
            padding: 28,
          }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 6px", color: "#fff" }}>{t.title}</h1>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 20 }}>{t.sub}</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>{t.firstName}</label>
                <input type="text" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{t.lastName}</label>
                <input type="text" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.username}</label>
              <input type="text" value={form.username} onChange={(e) => set("username", e.target.value.toLowerCase())} style={inputStyle} placeholder="josevazquez" />
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{t.usernameHint}</div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.email}</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} style={inputStyle} />
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{t.emailHint}</div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.phone}</label>
              <div style={{
                /* CSS overrides to make the light-theme PhoneField (esp. the
                   country dropdown popup, which uses CSS vars internally)
                   readable on the dark RegisterPage background. */
                "--color-bg-input": "rgba(255,255,255,0.06)",
                "--color-border": "rgba(255,255,255,0.15)",
                "--color-text": "#fff",
                "--color-text-secondary": "rgba(255,255,255,0.7)",
                "--color-text-muted": "rgba(255,255,255,0.5)",
                "--color-bg-card-solid": "#152A40",
                "--color-bg": "#0D1B2A",
                "--color-accent": "#5BBCB0",
              }}>
                <PhoneField
                  country={dialToCountryCode(form.phoneCountryCode || "52")}
                  value={serializePhone(dialToCountryCode(form.phoneCountryCode || "52"), form.phoneAreaCode, form.phoneNumber)}
                  onChange={(s, iso) => {
                    const p = parsePhoneShared(s);
                    const chosenIso = iso || p.country;
                    set("phoneCountryCode", countryCodeToDialDigits(chosenIso));
                    set("phoneAreaCode", p.area);
                    set("phoneNumber", p.local);
                  }}
                  lang={lang}
                  inputStyle={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.dob}</label>
              <input type="date" value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} style={inputStyle} max={new Date().toISOString().slice(0, 10)} />
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{t.dobHint}</div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.password}</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? t.hidePassword : t.showPassword}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer",
                    color: "rgba(255,255,255,0.6)", fontSize: 16, padding: 4,
                  }}
                >{showPassword ? "🙈" : "👁"}</button>
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{t.passwordRules}</div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.confirmPassword}</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={(e) => set("confirmPassword", e.target.value)}
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  title={showConfirmPassword ? t.hidePassword : t.showPassword}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer",
                    color: "rgba(255,255,255,0.6)", fontSize: 16, padding: 4,
                  }}
                >{showConfirmPassword ? "🙈" : "👁"}</button>
              </div>
            </div>

            {error && (
              <div style={{
                marginTop: 16, padding: 10, background: "rgba(231,76,60,0.12)",
                border: "1px solid rgba(231,76,60,0.4)", borderRadius: 8, color: "#FCA5A5", fontSize: 12,
              }}>{error}</div>
            )}

            <button onClick={handleSubmit} disabled={submitting} style={{
              marginTop: 20, width: "100%", padding: "12px", borderRadius: 8, border: "none",
              background: submitting ? ACCENT_DARK : ACCENT, color: NAVY,
              fontSize: 14, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer",
              textTransform: "uppercase", letterSpacing: "0.08em",
            }}>{submitting ? t.submitting : t.submit}</button>
          </div>
        )}
      </div>
    </div>
  );
}
